import { filter } from 'lodash'
import {
  DataMatchFolderStruct,
  DataMatchOutput,
  DataMatchStatus,
  DateFormat,
  DynamoFile,
  EPlusFolder,
  TestFileCheck,
  TextBlock,
  UserSettings,
} from '../types'
import { isTextBlock } from './guards'
import { getFileAndOcrBatch } from './common'
import { getColumnSize, lettersToNumber } from '../workbook'
import { getAllFileIdsInAFolder } from './file_management'
import { extractAndParseDateWithYearValidation, isValidDate } from './date-util'
import {
  isPossiblyNumeric,
  containsCurrencySymbol,
  extractNumericValueFromAmount,
} from './currency'
import { USER_SETTINGS, LARGE_NUMBER } from '../constant'

export const formatDataMatchResult = (
  result: (string | null | TextBlock)[][]
): any[][] => {
  const settings = localStorage.getItem(USER_SETTINGS)
  const userSettings = settings ? (JSON.parse(settings) as UserSettings) : null
  return result.map((row) => {
    return row.map((value) => {
      if (typeof value === 'string') return value
      else if (isTextBlock(value)) {
        if (
          isValidDate(
            value.description,
            userSettings ? userSettings.dateFormat : DateFormat.US
          ).valid
        ) {
          const extractedDate = extractAndParseDateWithYearValidation(
            value.description,
            DateFormat.AU
          )
          if (extractedDate) {
            return extractedDate
          }
        }
        if (
          isPossiblyNumeric(value.description) ||
          containsCurrencySymbol(value.description)
        ) {
          const currencyValue = extractNumericValueFromAmount(value.description)
          if (!isNaN(currencyValue)) return currencyValue
        }
        return value.description
      } else {
        return ''
      }
    })
  })
}

export const getInputFiles = async (
  files: TestFileCheck[],
  isLocalMode: boolean
) => {
  const inputFiles = filter(files, (file) => file.checked)
  const fileIds = inputFiles.map((file) => file.id)
  const results = await getFileAndOcrBatch(fileIds, isLocalMode)
  const resultHashmap = new Map<string, (typeof results)[0]>()
  results.forEach((result) => {
    const [id, , ,] = result
    resultHashmap.set(id, result)
  })

  inputFiles.forEach((file) => {
    const fileData = resultHashmap.get(file.id)
    if (!fileData) throw new Error(`Internal error: file ${file.id} is invalid`)
    const [, , ocr, dateCache] = fileData
    file.ocrJson = ocr
    file.dateCache = dateCache
  })

  return inputFiles
}

export const getDataMatchFolderStruct = async ({
  data,
  folders,
  inputRange,
  outputRange,
  selectedFolders,
}: {
  inputRange: string
  outputRange: string[]
  selectedFolders: string[]
  folders: EPlusFolder[]
  data: DynamoFile[]
}) => {
  const inputColCount = await getColumnSize(inputRange)

  const outputRanges: string[][] = []
  for (let i = 0; i < outputRange.length; i += inputColCount) {
    const slice = outputRange.slice(i, inputColCount + i)
    outputRanges.push(slice)
  }

  const dataMatchFolderStructs: DataMatchFolderStruct[] = []
  for (const selectedFolderId of selectedFolders) {
    const folder = folders.find((folder) => folder.fileId === selectedFolderId)
    if (!folder) return []
    const fileIds = getAllFileIdsInAFolder(folder.fileId, data)
    dataMatchFolderStructs.push({
      id: folder.fileId,
      fileIds,
      name: folder.folderName,
      outputColumns: [],
      result: [],
      formattedResult: [],
      excelRanges: [],
      numberFormat: [],
    })
  }

  for (let i = 0; i < outputRanges.length; i++) {
    dataMatchFolderStructs[i].outputColumns.push(...outputRanges[i])
  }
  return dataMatchFolderStructs
}

export const getExcelOutputRanges = ({
  filteredOutputRange,
  formattedResult,
  inputRange,
  worksheet,
}: {
  formattedResult: any[][]
  filteredOutputRange: string[]
  inputRange: Excel.Range
  worksheet: Excel.Worksheet
}) => {
  const outputRanges: Excel.Range[][] = []

  for (let i = 0; i < formattedResult.length; i++) {
    const row: Excel.Range[] = []
    for (let j = 0; j < filteredOutputRange.length; j++) {
      const letter = lettersToNumber(filteredOutputRange[j])
      const range = worksheet.getRangeByIndexes(
        inputRange.rowIndex + i,
        letter - 1,
        1,
        1
      )
      range.load(['address', 'rowIndex', 'columnIndex', 'values'])
      row.push(range)
    }
    outputRanges.push(row)
  }

  return outputRanges
}

export const removeSpaces = (str: string) => {
  return str.replace(/\s+/g, '')
}

export const processLargeNumberValueAsText = (value: any) => {
  // Convert large numbers to strings before setting the cell value
  let processedValue = value
  if (typeof processedValue === 'number' && Math.abs(processedValue) >= LARGE_NUMBER) {
    processedValue = processedValue.toString()
  } else if (typeof processedValue === 'string' && !isNaN(Number(processedValue))) {
    const numValue = Number(processedValue)
    if (Math.abs(numValue) >= LARGE_NUMBER) {
      return value
    }
  }
  return processedValue
}

export const formatDataMatchResultWithDuplicateHandling = (
  result: DataMatchOutput
): any[][] => {
  return result.map((re, i) => {
    if (re.length > 1) {
      const returnArray: any[] = re[0].map((_) => null)
      for (let j = 0; j < returnArray.length; j++) {
        const allNull = re.every((val) => val[j] === null)
        if (allNull) {
          returnArray[j] = null
        } else {
          const noneNullCount = re.filter((val) => val[j] !== null).length
          let noneNull: any = null
          if (noneNullCount > 1) {
            noneNull = DataMatchStatus.DUPLICATE
          } else {
            noneNull = re.find((val) => val[j] !== null)
          }
          if (noneNull === DataMatchStatus.DUPLICATE) {
            returnArray[j] = noneNull
          } else if (noneNull === null) {
            returnArray[j] = null
          } else if (isTextBlock(noneNull)) {
            if (isValidDate(noneNull.description, DateFormat.AU).valid) {
              const extractedDate = extractAndParseDateWithYearValidation(
                noneNull.description,
                DateFormat.AU
              )
              if (extractedDate) {
                returnArray[j] = extractedDate
              } else if (
                isPossiblyNumeric(noneNull.description) ||
                containsCurrencySymbol(noneNull.description)
              ) {
                const currencyValue = extractNumericValueFromAmount(
                  noneNull.description
                )
                if (!isNaN(currencyValue)) returnArray[j] = currencyValue
                else returnArray[j] = noneNull.description
              } else {
                returnArray[j] = noneNull.description
              }
            }
          } else returnArray[j] = ''
        }
      }
      return returnArray
    }
    return re[0].map((val) => {
      if (val == null) return ''
      else if (typeof val === 'string') return val
      else if (isTextBlock(val)) {
        if (isValidDate(val.description, DateFormat.AU).valid) {
          const extractedDate = extractAndParseDateWithYearValidation(
            val.description,
            DateFormat.AU
          )
          if (extractedDate) {
            return extractedDate
          }
        }
        if (
          isPossiblyNumeric(val.description) ||
          containsCurrencySymbol(val.description)
        ) {
          const currencyValue = extractNumericValueFromAmount(val.description)
          if (!isNaN(currencyValue)) return currencyValue
        }
        return val.description
      } else return ''
    })
  })
}
